import httpRequest from "@/auth/http-client";
import { generatePaymentHash, getAccessToken } from "@/helpers/auth-helpers";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import {
  AccountAnticipationConfiguration,
  AccountBalance,
  AccountFees,
  AccountStatusResponse,
  ActivationFormData,
  BankAccount,
  BankAccountInput,
  CommercialInfo,
  CommercialInfoInput,
  FinancialTransaction,
  FinancialTransactionBase64,
  IBank,
  PaymentPayload,
  Talk2PayDocument,
  Talk2PayFees,
} from "@/interfaces/talk2pay.iterfaces";
import {
  AvailablePaymentType,
  PaymentType,
  PaymentTypeCreateInput,
  PaymentTypeUpdateInput,
} from "@/interfaces/payment-type.interface";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

class T2PayService {
  private baseUrl = "api/v1/talk2pay/onboarding";
  private baseAccountUrl = "api/v1/talk2pay/account";

  async create(formData: ActivationFormData): Promise<void> {
    await httpRequest.post(this.baseUrl, formData);
  }

  async getDocumentation(
    pagination: RequestPagination
  ): Promise<PaginatedResponse<Talk2PayDocument>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.get(`${this.baseAccountUrl}/documents?${paginationQuery}`);
    return response;
  }

  async getBankAccounts(pagination: RequestPagination): Promise<PaginatedResponse<BankAccount>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.get(`api/v1/talk2pay/beneficiaryaccount?${paginationQuery}`);
    return response;
  }

  async getBanks(pagination: RequestPagination): Promise<PaginatedResponse<IBank>> {
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await httpRequest.get(`api/v1/talk2pay/banks?${paginationQuery}`);
    return response;
  }

  async getAccountBalance(): Promise<AccountBalance> {
    const response = await httpRequest.get(`${this.baseAccountUrl}/balance`);
    return response;
  }

  async getAccountStatus(): Promise<AccountStatusResponse> {
    const response = await httpRequest.get(`${this.baseAccountUrl}/status`);
    return response;
  }

  async getCommercialInfo(): Promise<CommercialInfo> {
    return await httpRequest.get(`${this.baseAccountUrl}/commercialInfo`);
  }

  async updateCommercialInfo(payload: CommercialInfoInput): Promise<CommercialInfo> {
    return await httpRequest.put(`${this.baseAccountUrl}/commercialInfo`, payload);
  }

  async getAccountFees(): Promise<AccountFees> {
    const response = await httpRequest.get(`${this.baseAccountUrl}/fees`);
    return response;
  }

  async getAccountAnticipation(): Promise<AccountAnticipationConfiguration> {
    return await httpRequest.get(`${this.baseAccountUrl}/anticipation`);
  }

  async updateAccountAnticipation(
    payload: AccountAnticipationConfiguration
  ): Promise<AccountAnticipationConfiguration> {
    return await httpRequest.put(`${this.baseAccountUrl}/anticipation`, payload);
  }

  async getTalk2PayFees(): Promise<Talk2PayFees> {
    const response = await httpRequest.get("api/v1/talk2pay/fees");
    return response;
  }

  async deleteBankAccount(bankAccountId: number): Promise<void> {
    await httpRequest.delete(`api/v1/talk2pay/beneficiaryaccount/${bankAccountId}`);
  }

  async createBankAccount(formData: BankAccountInput): Promise<BankAccount> {
    const response = await httpRequest.post("api/v1/talk2pay/beneficiaryaccount", formData);
    return response;
  }

  async getFinancialTransaction({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }): Promise<FinancialTransaction> {
    const response = await httpRequest.get(
      `${this.baseAccountUrl}/financialTransaction?StartDate=${startDate.toISOString()}&FinishDate=${endDate.toISOString()}`
    );
    return response;
  }

  async createTransfer(formData: PaymentPayload, customerId: number): Promise<void> {
    const token = getAccessToken()!;
    const timestamp = Math.floor(Date.now() / 1000);
    const hash = await generatePaymentHash(timestamp, formData, customerId);

    const headers = {
      Authorization: `Bearer ${token}`,
      CustomerId: customerId,
      Timestamp: timestamp.toString(),
      Hash: hash,
    };

    await httpRequest.post("api/v1/talk2pay/transfer", formData, { headers });
  }

  async getFinancialTransactionBase64({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }): Promise<FinancialTransactionBase64 | { noContent: true }> {
    const { data, status } = await httpRequest.getWithResponse<FinancialTransactionBase64>(
      `${this.baseAccountUrl}/financialTransaction/pdf?StartDate=${startDate.toISOString()}&FinishDate=${endDate.toISOString()}`
    );
    if (status === 204 || !data?.base64) {
      return { noContent: true };
    }
    return data;
  }

  // ===== Payment Types =====
  private paymentTypeUrl = "api/v1/talk2pay/paymenttype";

  async getPaymentTypes(): Promise<PaymentType[]> {
    return await httpRequest.get(this.paymentTypeUrl);
  }

  async getAvailablePaymentTypes(): Promise<AvailablePaymentType[]> {
    return await httpRequest.get(`${this.paymentTypeUrl}/available`);
  }

  async getPaymentTypeById(id: number): Promise<PaymentType | null> {
    const { data, status } = await httpRequest.getWithResponse<PaymentType>(
      `${this.paymentTypeUrl}/${id}`
    );
    if (status === 204) return null;
    return data;
  }

  async createPaymentType(formData: PaymentTypeCreateInput): Promise<PaymentType> {
    return await httpRequest.post(this.paymentTypeUrl, formData);
  }

  async updatePaymentType(formData: PaymentTypeUpdateInput, id: number): Promise<PaymentType> {
    return await httpRequest.put(`${this.paymentTypeUrl}/${id}`, formData);
  }

  async deletePaymentType(id: number): Promise<void> {
    await httpRequest.delete(`${this.paymentTypeUrl}/${id}`);
  }
}

const t2payService = new T2PayService();
export default t2payService;
